// MARK://dhk8/admin-shell/pages/modules/WarehouseDetail
// Ficha de un almacén (EntityDetailPage) con sus KPIs + drill-in de los insumos /
// existencias que viven DENTRO, en DataTable. Desde aquí se trabaja el stock del
// almacén sin salir: agregar insumo, ajustar a una cantidad exacta, editar (unidad +
// mínimo), transferir a otro almacén y eliminar. Drill-in de /inventory/warehouses.
//
// DS RBAC (Gustavo 2026-06-22): cabecera = EntityDetailPage (título + StatusPill +
// SummaryStrip). Lista interna = DataTable + FilterBar. Ajuste de existencia por modal
// "Ajustar a N" (no +/−1); el delta = nuevo − actual se manda a adjustStock, así la
// matemática de stock no cambia. Borrar = ConfirmDialog; feedback = Toast.
//
// El subgrafo erp-inventory ya expone updateWarehouse/deleteWarehouse → la ficha
// edita (nombre/código/activo) y elimina el almacén EN SÍ, además del STOCK que vive
// dentro. Editar = SectionedSheet; eliminar = ConfirmDialog + toast + volver a la lista.
import { useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Pencil, Trash2, ArrowLeftRight, Calculator } from 'lucide-react'
import * as inv from '../../lib/clients/inventory.js'
import * as cat from '../../lib/clients/catalog.js'
import type { InventoryItem } from '../../lib/mock-data.js'
import { useAsync, asyncState } from '../../lib/use-async.js'
import { mxn } from '../../lib/money.js'
import SectionedSheet, { FormSection } from '../../components/SectionedSheet.js'
import UnitPicker, { unitShort } from '../../components/UnitPicker.js'
import EntityDetailPage from '../../components/EntityDetailPage.js'
import DataTable, { type DataTableColumn, type SortState } from '../../components/DataTable.js'
import FilterBar from '../../components/FilterBar.js'
import Tabs from '../../components/Tabs.js'
import AuditTrail from '../../components/AuditTrail.js'
import { SummaryStrip } from '../../components/StatCard.js'
import StatusPill, { lifecycleTone, type StatusTone } from '../../components/StatusPill.js'
import { useConfirm } from '../../components/ConfirmDialog.js'
import { useToast } from '../../components/Toast.js'

const STATUS_TONE: Record<string, StatusTone> = { OK: 'ok', LOW: 'warn', OUT: 'danger' }
const STATUS_LABEL: Record<string, string> = { OK: 'OK', LOW: 'Bajo', OUT: 'Agotado' }

function Qty({ value, unit }: { value: number; unit?: string }) {
  return <span className="adm-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>{value}{unit ? ` ${unitShort(unit)}` : ''}</span>
}

export default function WarehouseDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const { toast } = useToast()
  const warehouses = useAsync((s) => inv.listWarehouses(s))
  const items = useAsync((s) => inv.listInventory(s, id), [id])
  // "Agregar insumo" lista INGREDIENT (no PRODUCT vendible): un almacén guarda
  // insumos/materia prima. listIngredients filtra type=INGREDIENT; el campo de la
  // mutación se llama productId porque los insumos SON filas product en erp-products.
  const ingredients = useAsync((s) => cat.listIngredients(s))
  const wh = (warehouses.data ?? []).find((w) => w.id === id)
  const otherWarehouses = (warehouses.data ?? []).filter((w) => w.id !== id)

  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const [creating, setCreating] = useState(false)
  const [createForm, setCreateForm] = useState<{ productId: string; unit: string; reorderLevel: number }>({ productId: '', unit: 'pza', reorderLevel: 0 })
  const [editing, setEditing] = useState<InventoryItem | null>(null)
  const [editForm, setEditForm] = useState<InventoryItem>({ id: '', name: '', unit: 'pza', onHand: 0, reorderLevel: 0, status: 'OK' })
  const [transferring, setTransferring] = useState<InventoryItem | null>(null)
  const [transferForm, setTransferForm] = useState<{ dstWarehouseId: string; qty: number }>({ dstWarehouseId: '', qty: 1 })
  const [adjustingItem, setAdjustingItem] = useState<InventoryItem | null>(null)
  const [adjustQty, setAdjustQty] = useState(0)

  // Editar el almacén EN SÍ (nombre/código/activo) → updateWarehouse.
  const [editingWh, setEditingWh] = useState(false)
  const [whForm, setWhForm] = useState<{ name: string; code: string; active: boolean }>({ name: '', code: '', active: true })

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [sort, setSort] = useState<SortState | undefined>(undefined)
  const [tab, setTab] = useState<'existencias' | 'historial'>('existencias')

  async function saveCreate() {
    setError(null)
    if (!createForm.productId) { setError('Selecciona un insumo.'); return }
    if (!createForm.unit.trim()) { setError('La unidad es obligatoria.'); return }
    setSaving(true)
    try {
      await inv.createItemWithRefs({ productId: createForm.productId, warehouseId: id, unit: createForm.unit, reorderLevel: createForm.reorderLevel })
      setCreating(false); setCreateForm({ productId: '', unit: 'pza', reorderLevel: 0 }); items.reload(); toast('Insumo agregado', 'success')
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo agregar el insumo.') } finally { setSaving(false) }
  }
  async function saveEdit() {
    setError(null); setSaving(true)
    try { await inv.updateItem(editForm); setEditing(null); items.reload(); toast('Insumo actualizado', 'success') }
    catch (e) { setError(e instanceof Error ? e.message : 'No se pudo actualizar.') } finally { setSaving(false) }
  }
  function openAdjust(it: InventoryItem) { setAdjustingItem(it); setAdjustQty(it.onHand); setError(null) }
  async function saveAdjust() {
    if (!adjustingItem) return
    // delta = nuevo − actual: la matemática de stock no cambia, solo el destino.
    const delta = Math.round((adjustQty - adjustingItem.onHand) * 100) / 100
    if (delta === 0) { setAdjustingItem(null); return }
    setError(null); setSaving(true)
    try { await inv.adjustStock(adjustingItem.id, delta); setAdjustingItem(null); items.reload(); toast('Existencia ajustada', 'success') }
    catch (e) { setError(e instanceof Error ? e.message : 'No se pudo ajustar.') } finally { setSaving(false) }
  }
  async function remove(it: InventoryItem) {
    const ok = await confirm({ title: `¿Quitar "${it.name}" de este almacén?`, message: 'El insumo se quitará de este almacén. No se puede deshacer.', tone: 'danger', confirmLabel: 'Quitar' })
    if (!ok) return
    setError(null)
    try { await inv.deleteItem(it.id); items.reload(); toast('Insumo eliminado', 'success') }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar.', 'danger') }
  }
  async function saveTransfer() {
    if (!transferring) return
    setError(null)
    if (!transferForm.dstWarehouseId) { setError('Selecciona el almacén destino.'); return }
    if (!(transferForm.qty > 0)) { setError('La cantidad debe ser mayor a 0.'); return }
    setSaving(true)
    try {
      await inv.transferStock({ stockItemId: transferring.id, qty: transferForm.qty, srcWarehouseId: id, dstWarehouseId: transferForm.dstWarehouseId })
      setTransferring(null); items.reload(); toast('Traspaso registrado', 'success')
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo transferir.') } finally { setSaving(false) }
  }

  function openEditWh() {
    if (!wh) return
    setWhForm({ name: wh.name, code: wh.code ?? '', active: wh.active }); setError(null); setEditingWh(true)
  }
  async function saveEditWh() {
    if (!wh) return
    setError(null)
    if (!whForm.name.trim()) { setError('El nombre es obligatorio.'); return }
    setSaving(true)
    try {
      await inv.updateWarehouse(wh.id, { name: whForm.name.trim(), code: whForm.code.trim() ? whForm.code.trim() : null, active: whForm.active })
      setEditingWh(false); warehouses.reload(); toast('Almacén actualizado', 'success')
    } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo actualizar el almacén.') } finally { setSaving(false) }
  }
  async function removeWh() {
    if (!wh) return
    const ok = await confirm({ title: `¿Eliminar "${wh.name}"?`, message: 'El almacén se eliminará. Esta acción no se puede deshacer.', tone: 'danger', confirmLabel: 'Eliminar' })
    if (!ok) return
    try { await inv.deleteWarehouse(wh.id); toast('Almacén eliminado', 'success'); navigate('/inventory/warehouses') }
    catch (e) { toast(e instanceof Error ? e.message : 'No se pudo eliminar el almacén.', 'danger') }
  }

  // KPIs del almacén: insumos / bajo mínimo / agotados / valor de inventario.
  const stats = useMemo(() => {
    const all = items.data ?? []
    const valueCents = all.reduce((sum, it) => sum + (it.unitCostCents ?? 0) * it.onHand, 0)
    return {
      total: all.length,
      low: all.filter((it) => it.status === 'LOW').length,
      out: all.filter((it) => it.status === 'OUT').length,
      valueCents,
    }
  }, [items.data])

  const rows = useMemo(() => {
    let r = items.data ?? []
    const q = search.trim().toLowerCase()
    if (q) r = r.filter((it) => it.name.toLowerCase().includes(q))
    if (statusFilter) r = r.filter((it) => it.status === statusFilter)
    if (sort) {
      const dir = sort.dir === 'asc' ? 1 : -1
      r = r.slice().sort((a, b) => {
        let av: number | string, bv: number | string
        switch (sort.key) {
          case 'name': av = a.name.toLowerCase(); bv = b.name.toLowerCase(); break
          case 'onHand': av = a.onHand; bv = b.onHand; break
          case 'available': av = a.onHand - a.reservedQty; bv = b.onHand - b.reservedQty; break
          case 'reorder': av = a.reorderLevel; bv = b.reorderLevel; break
          default: return 0
        }
        return av < bv ? -dir : av > bv ? dir : 0
      })
    }
    return r
  }, [items.data, search, statusFilter, sort])

  const columns: DataTableColumn<inv.StockItemDetail>[] = [
    { key: 'name', header: 'Insumo', sortable: true, render: (it) => <span style={{ fontWeight: 'var(--pos-weight-medium, 500)' }}>{it.name}</span> },
    { key: 'onHand', header: 'Existencia', align: 'right', sortable: true, render: (it) => <Qty value={it.onHand} unit={it.unit} /> },
    { key: 'available', header: 'Disponible', align: 'right', sortable: true, render: (it) => <Qty value={Math.round((it.onHand - it.reservedQty) * 100) / 100} unit={it.unit} /> },
    { key: 'reorder', header: 'Mínimo', align: 'right', sortable: true, render: (it) => <Qty value={it.reorderLevel} unit={it.unit} /> },
    { key: 'estado', header: 'Estado', render: (it) => <StatusPill tone={STATUS_TONE[it.status]} label={STATUS_LABEL[it.status]} /> },
    {
      key: 'acciones', header: '', align: 'right', render: (it) => (
        <div style={{ display: 'inline-flex', gap: 4 }} onClick={(e) => e.stopPropagation()}>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => openAdjust(it)} aria-label="Ajustar existencia" title="Ajustar a una cantidad exacta"><Calculator size={15} /></button>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => { setEditForm({ ...it }); setError(null); setEditing(it) }} aria-label="Editar"><Pencil size={15} /></button>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => { setTransferForm({ dstWarehouseId: '', qty: 1 }); setError(null); setTransferring(it) }} aria-label="Transferir" title="Transferir a otro almacén" disabled={otherWarehouses.length === 0}><ArrowLeftRight size={15} /></button>
          <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => remove(it)} aria-label="Eliminar"><Trash2 size={15} /></button>
        </div>
      ),
    },
  ]

  return (
    <div className="adm-page">
      <Link to="/inventory/warehouses" className="adm-row__meta" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 12, textDecoration: 'none' }}><ArrowLeft size={14} aria-hidden /> Almacenes</Link>

      {error && <div className="adm-banner adm-banner--danger" role="alert" style={{ marginBottom: 12 }}>{error}</div>}

      <EntityDetailPage
        title={wh ? wh.name : 'Almacén'}
        subtitle={wh?.code ? `Código ${wh.code} · Insumos y existencias en este almacén` : 'Insumos y existencias en este almacén'}
        pills={wh ? <StatusPill tone={lifecycleTone(wh.active)} label={wh.active ? 'Activo' : 'Inactivo'} /> : undefined}
        kpis={items.data ? <SummaryStrip items={[
          { label: 'Insumos', value: stats.total },
          { label: 'Bajo mínimo', value: stats.low, tone: stats.low ? 'warn' : 'neutral' },
          { label: 'Agotados', value: stats.out, tone: stats.out ? 'danger' : 'neutral' },
          { label: 'Valor inventario', value: mxn(stats.valueCents) },
        ]} /> : undefined}
        actions={<>
          <button className="adm-btn adm-btn--sm" onClick={openEditWh} disabled={!wh}><Pencil size={15} /> Editar</button>
          <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={removeWh} disabled={!wh}><Trash2 size={15} /> Eliminar</button>
          <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => { setCreateForm({ productId: '', unit: 'pza', reorderLevel: 0 }); setError(null); setCreating(true) }}>
            <Plus size={16} /> Agregar insumo
          </button>
        </>}
        tabs={<Tabs
          tabs={[
            { id: 'existencias', label: 'Existencias' },
            { id: 'historial', label: 'Historial' },
          ]}
          active={tab}
          onChange={(t) => setTab(t as 'existencias' | 'historial')}
          ariaLabel="Secciones del almacén"
        />}
      >
        {tab === 'existencias' && <>
          {asyncState(items.loading, items.error, items.reload)}

          {items.data && (
            <DataTable<inv.StockItemDetail>
              columns={columns}
              rows={rows}
              getRowKey={(it) => it.id}
              zebra
              sort={sort ?? null}
              onSortChange={setSort}
              emptyText={items.data.length === 0 ? 'Este almacén no tiene insumos. Agrega el primero.' : 'Ningún insumo coincide con el filtro.'}
              toolbar={<FilterBar
                search={search}
                onSearch={setSearch}
                searchPlaceholder="Buscar insumo…"
                filters={[{ key: 'status', label: 'Estado', value: statusFilter, onChange: setStatusFilter, options: [{ value: '', label: 'Todos' }, { value: 'OK', label: 'OK' }, { value: 'LOW', label: 'Bajo' }, { value: 'OUT', label: 'Agotado' }] }]}
              />}
            />
          )}
        </>}

        {tab === 'historial' && <AuditTrail entityId={id} />}
      </EntityDetailPage>

      {/* Editar el almacén EN SÍ (nombre/código/activo) */}
      <SectionedSheet open={editingWh} title="Editar almacén" onClose={() => { setEditingWh(false); setError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setEditingWh(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveEditWh} disabled={saving || !whForm.name.trim()}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </>}>
        <FormSection title="Identificación">
          <label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" value={whForm.name} onChange={(e) => setWhForm({ ...whForm, name: e.target.value })} placeholder="Almacén principal" /></label>
          <label className="adm-field"><span className="adm-field__label">Código (opcional)</span><input className="adm-input" value={whForm.code} onChange={(e) => setWhForm({ ...whForm, code: e.target.value })} placeholder="MAIN" /></label>
        </FormSection>
        <FormSection title="Estado">
          <label className="adm-field"><span className="adm-field__label">Estado</span>
            <select className="adm-select" value={whForm.active ? 'active' : 'inactive'} onChange={(e) => setWhForm({ ...whForm, active: e.target.value === 'active' })} aria-label="Estado del almacén">
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </label>
        </FormSection>
      </SectionedSheet>

      {/* Agregar insumo a ESTE almacén */}
      <SectionedSheet open={creating} title={`Agregar insumo${wh ? ` · ${wh.name}` : ''}`} onClose={() => { setCreating(false); setError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setCreating(false)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveCreate} disabled={saving || !createForm.productId || !createForm.unit.trim()}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </>}>
        <FormSection title="Insumo">
          <label className="adm-field"><span className="adm-field__label">Insumo</span>
            <select className="adm-select" value={createForm.productId} onChange={(e) => setCreateForm({ ...createForm, productId: e.target.value })} aria-label="Insumo">
              <option value="">— Selecciona insumo —</option>
              {(ingredients.data ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}{p.sku ? ` · ${p.sku}` : ''}</option>)}
            </select>
          </label>
        </FormSection>
        <FormSection title="Unidad y mínimo">
          <UnitPicker label="Unidad" value={createForm.unit} onChange={(unit) => setCreateForm({ ...createForm, unit })} />
          <label className="adm-field"><span className="adm-field__label">Mínimo</span><input className="adm-input" type="number" min={0} value={createForm.reorderLevel} onChange={(e) => setCreateForm({ ...createForm, reorderLevel: Number(e.target.value) })} /></label>
        </FormSection>
      </SectionedSheet>

      {/* Editar insumo */}
      <SectionedSheet open={editing != null} title="Editar insumo" onClose={() => { setEditing(null); setError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setEditing(null)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveEdit} disabled={saving || !editForm.unit.trim()}>{saving ? 'Guardando…' : 'Guardar'}</button>
        </>}>
        <FormSection title="Identificación">
          <label className="adm-field"><span className="adm-field__label">Nombre</span><input className="adm-input" value={editForm.name} disabled aria-readonly="true" /></label>
        </FormSection>
        <FormSection title="Medida y reorden">
          <UnitPicker label="Unidad" value={editForm.unit} onChange={(unit) => setEditForm({ ...editForm, unit })} />
          <label className="adm-field"><span className="adm-field__label">Mínimo</span><input className="adm-input" type="number" min={0} value={editForm.reorderLevel} onChange={(e) => setEditForm({ ...editForm, reorderLevel: Number(e.target.value) })} /></label>
          <p className="adm-row__meta">Las existencias se ajustan con “Ajustar a una cantidad exacta” en la lista.</p>
        </FormSection>
      </SectionedSheet>

      {/* Ajustar a N */}
      <SectionedSheet open={adjustingItem != null} title="Ajustar a una cantidad" onClose={() => { setAdjustingItem(null); setError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setAdjustingItem(null)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveAdjust} disabled={saving}>{saving ? 'Ajustando…' : 'Ajustar'}</button>
        </>}>
        {adjustingItem && (
          <FormSection title="Nueva existencia">
            <p className="adm-row__meta" style={{ marginBottom: 0 }}>{adjustingItem.name} — existencia actual <span className="adm-mono">{adjustingItem.onHand} {unitShort(adjustingItem.unit)}</span></p>
            <label className="adm-field"><span className="adm-field__label">Fijar existencia a ({unitShort(adjustingItem.unit)})</span><input className="adm-input" type="number" min={0} value={adjustQty} onChange={(e) => setAdjustQty(Number(e.target.value))} autoFocus /></label>
            <p className="adm-row__meta">Se registrará un movimiento de ajuste de <span className="adm-mono">{Math.round((adjustQty - adjustingItem.onHand) * 100) / 100} {unitShort(adjustingItem.unit)}</span> (nuevo − actual).</p>
          </FormSection>
        )}
      </SectionedSheet>

      {/* Transferir a otro almacén */}
      <SectionedSheet open={transferring != null} title="Transferir a otro almacén" onClose={() => { setTransferring(null); setError(null) }}
        footer={<>
          <button className="adm-btn" onClick={() => setTransferring(null)}>Cancelar</button>
          <button className="adm-btn adm-btn--primary" onClick={saveTransfer} disabled={saving || !transferForm.dstWarehouseId || !(transferForm.qty > 0)}>{saving ? 'Transfiriendo…' : 'Transferir'}</button>
        </>}>
        {transferring && <p className="adm-row__meta" style={{ marginBottom: 8 }}>{transferring.name} — disponible <span className="adm-mono">{transferring.onHand} {unitShort(transferring.unit)}</span></p>}
        <FormSection title="Destino">
          <label className="adm-field"><span className="adm-field__label">Almacén destino</span>
            <select className="adm-select" value={transferForm.dstWarehouseId} onChange={(e) => setTransferForm({ ...transferForm, dstWarehouseId: e.target.value })} aria-label="Almacén destino">
              <option value="">— Selecciona almacén —</option>
              {otherWarehouses.map((w) => <option key={w.id} value={w.id}>{w.name}{w.code ? ` (${w.code})` : ''}</option>)}
            </select>
          </label>
          <label className="adm-field"><span className="adm-field__label">Cantidad a mover</span><input className="adm-input" type="number" min={1} value={transferForm.qty} onChange={(e) => setTransferForm({ ...transferForm, qty: Number(e.target.value) })} /></label>
        </FormSection>
      </SectionedSheet>
    </div>
  )
}
